/**
 * Research Service - handles fetching and processing prospect research data.
 * Fetches via Vercel Edge Function, processes client-side, uses AI for synthesis.
 */

import { getAIService } from '../stores/aiStore'
import { AIOutputSchema } from '../types/research'
import type {
  WebsiteData,
  CompaniesHouseData,
  CompanyIntelligence,
  AIOutput,
  DataSource,
  ResearchRawData,
} from '../types/research'

const RESEARCH_ENDPOINT = '/api/research'

// Custom error for research operations
export class ResearchError extends Error {
  constructor(
    message: string,
    public readonly source?: string,
    public readonly retryable: boolean = false
  ) {
    super(message)
    this.name = 'ResearchError'
  }
}

// Fetch website data via Edge Function
export async function fetchWebsiteData(domain: string): Promise<WebsiteData | null> {
  try {
    const response = await fetch(RESEARCH_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type: 'website', domain }),
    })

    if (response.status === 429) {
      throw new ResearchError('Rate limit exceeded. Please wait a moment.', 'website', true)
    }

    const result = await response.json()

    if (!result.success) {
      console.warn('Website fetch failed:', result.error)
      return null
    }

    // Parse HTML client-side
    const parser = new DOMParser()
    const doc = parser.parseFromString(result.data.html, 'text/html')

    return {
      url: result.data.url,
      fetchedAt: result.data.fetchedAt,
      title: doc.querySelector('title')?.textContent || null,
      metaDescription: doc.querySelector('meta[name="description"]')?.getAttribute('content') || null,
      ogTitle: doc.querySelector('meta[property="og:title"]')?.getAttribute('content') || null,
      ogDescription: doc.querySelector('meta[property="og:description"]')?.getAttribute('content') || null,
      ogImage: doc.querySelector('meta[property="og:image"]')?.getAttribute('content') || null,
      schemaOrg: extractSchemaOrg(doc),
      bodyText: extractBodyText(doc, 5000),
    }
  } catch (error) {
    if (error instanceof ResearchError) throw error
    console.error('Website fetch error:', error)
    return null
  }
}

function extractBodyText(doc: Document, maxLength: number): string {
  const clone = doc.body?.cloneNode(true) as HTMLElement
  if (!clone) return ''

  // Remove non-content elements
  clone.querySelectorAll('script, style, nav, footer, header, aside, noscript').forEach(el => el.remove())

  return (clone.textContent || '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, maxLength)
}

function extractSchemaOrg(doc: Document): object[] {
  const schemas: object[] = []

  doc.querySelectorAll('script[type="application/ld+json"]').forEach(script => {
    try {
      schemas.push(JSON.parse(script.textContent || ''))
    } catch {
      // Ignore malformed JSON
    }
  })

  return schemas
}

// Fetch Companies House data via Edge Function
export async function fetchCompaniesHouseData(
  companyName: string,
  companyNumber?: string
): Promise<CompaniesHouseData | null> {
  try {
    const response = await fetch(RESEARCH_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'companiesHouse',
        companyName,
        companyNumber,
      }),
    })

    if (response.status === 429) {
      throw new ResearchError('Rate limit exceeded. Please wait a moment.', 'companiesHouse', true)
    }

    const result = await response.json()

    if (!result.success) {
      const errorMsg = result.error || 'Unknown error'
      console.warn('Companies House fetch failed:', errorMsg)
      
      // Throw meaningful errors so the user knows what's wrong
      if (errorMsg.includes('invalid') || errorMsg.includes('expired')) {
        throw new ResearchError('Companies House API key is invalid or expired. Check Vercel environment variables.', 'companiesHouse')
      }
      if (errorMsg.includes('forbidden')) {
        throw new ResearchError('Companies House API access forbidden. Check API key permissions.', 'companiesHouse')
      }
      if (errorMsg.includes('rate limit')) {
        throw new ResearchError('Companies House rate limit exceeded. Try again later.', 'companiesHouse', true)
      }
      
      return null
    }

    return result.data
  } catch (error) {
    if (error instanceof ResearchError) throw error
    console.error('Companies House fetch error:', error)
    throw new ResearchError(
      error instanceof Error ? error.message : 'Companies House lookup failed',
      'companiesHouse'
    )
  }
}

// Generate AI summary from raw data
export async function generateResearchSummary(
  company: { name: string; domain?: string },
  data: { website: WebsiteData | null; companiesHouse: CompaniesHouseData | null },
  businessContext?: string
): Promise<AIOutput> {
  const aiService = getAIService()

  if (!aiService) {
    throw new ResearchError('AI provider not configured', 'ai')
  }

  const context = buildContext(company, data)
  
  // Build system prompt based on whether user has provided business context
  const hasBusinessContext = businessContext && businessContext.trim().length > 0
  
  const systemPrompt = hasBusinessContext
    ? `You are a research analyst helping assess prospective clients. The user's business: "${businessContext}". Evaluate fit based on how well prospects match their services. Return ONLY valid JSON matching the specified schema.`
    : `You are a research analyst helping assess prospective clients. Return ONLY valid JSON matching the specified schema.`

  const fitInstructions = hasBusinessContext
    ? `Based on the user's business ("${businessContext}"), assess how well this prospect fits as a potential client.`
    : `Assess this company as a general business prospect.`

  const userPrompt = `Based on the following raw data about "${company.name}", generate a structured intelligence report.

${context}

${fitInstructions}

Return a JSON object with this exact structure:

{
  "overview": "2-3 sentence summary of what this company does, their market position, and any notable characteristics.",
  
  "sector": "Industry sector in plain English (e.g. 'Ecommerce / Fashion', 'B2B SaaS', 'Professional Services') or null if unknown",
  
  "growthSignals": ["Array of observed growth indicators. Empty array if none observed."],
  
  "digital": {
    "platform": "Detected platform (e.g. 'Shopify', 'WordPress', 'Custom') or null",
    "techDetected": ["Array of detected technologies from scripts/meta tags"],
    "mobileExperience": "Assessment of mobile approach or null"
  },
  
  "fitAssessment": {
    "fitScore": 1-5,
    "fitRationale": "One sentence explaining the fit score${hasBusinessContext ? ' in relation to the user\'s services' : ''}",
    "primaryNeeds": ["Array of 2-4 likely needs or challenges this company has${hasBusinessContext ? ' that relate to the user\'s services' : ''}"],
    "opportunities": ["Array of 3-5 specific ways ${hasBusinessContext ? 'the user could help this company' : 'to engage with this company'}"]
  },
  
  "outreachAngles": [
    {
      "hook": "Short name for this angle (3-5 words)",
      "message": "A specific, personalised outreach message (2-3 sentences max)${hasBusinessContext ? ' relevant to the user\'s services' : ''}"
    }
  ]
}

Guidelines:
- Be specific to THIS company, not generic advice
- If you can't determine something, use null or empty array
- For fitScore: 1=poor fit, 2=weak, 3=moderate, 4=strong, 5=excellent${hasBusinessContext ? ' for the user\'s specific services' : ''}
- Provide 2-3 outreach angles with different hooks
- Only list tech/signals you have evidence for

Return ONLY the JSON object, no markdown formatting or explanation.`

  const response = await aiService.complete([
    { role: 'system', content: systemPrompt },
    { role: 'user', content: userPrompt },
  ])

  return parseAndValidateAIResponse(response)
}

function buildContext(
  company: { name: string; domain?: string },
  data: { website: WebsiteData | null; companiesHouse: CompaniesHouseData | null }
): string {
  const sections: string[] = []

  if (data.website) {
    sections.push(`### Website Data
URL: ${data.website.url}
Title: ${data.website.title || 'Not found'}
Description: ${data.website.metaDescription || 'Not found'}
Content excerpt: ${data.website.bodyText || 'Not available'}`)
  }

  if (data.companiesHouse?.company) {
    const ch = data.companiesHouse.company
    sections.push(`### Companies House Data
Registered name: ${ch.company_name}
Company number: ${ch.company_number}
Status: ${ch.company_status}
Incorporated: ${ch.date_of_creation}
Type: ${ch.type}
SIC codes: ${ch.sic_codes?.join(', ') || 'Not listed'}`)
  }

  if (sections.length === 0) {
    sections.push(`Limited data available. Only company name provided: "${company.name}"`)
  }

  return sections.join('\n\n')
}

function parseAndValidateAIResponse(response: string): AIOutput {
  // Clean response (remove markdown code fences if present)
  let cleaned = response.trim()
  if (cleaned.startsWith('```')) {
    cleaned = cleaned.replace(/^```(?:json)?\n?/, '').replace(/\n?```$/, '')
  }

  // Parse JSON
  let parsed: unknown
  try {
    parsed = JSON.parse(cleaned)
  } catch {
    throw new ResearchError('AI response is not valid JSON', 'ai')
  }

  // Validate with Zod
  const result = AIOutputSchema.safeParse(parsed)

  if (!result.success) {
    console.error('AI output validation failed:', result.error.issues)
    return constructDefaultAIOutput(parsed)
  }

  return result.data
}

function constructDefaultAIOutput(partial: unknown): AIOutput {
  const p = (partial as Record<string, unknown>) || {}
  const digital = (p.digital as Record<string, unknown>) || {}
  const fitAssessment = (p.fitAssessment as Record<string, unknown>) || {}

  return {
    overview: typeof p.overview === 'string' ? p.overview : 'No overview available.',
    sector: typeof p.sector === 'string' ? p.sector : null,
    growthSignals: Array.isArray(p.growthSignals)
      ? p.growthSignals.filter((s): s is string => typeof s === 'string')
      : [],
    digital: {
      platform: typeof digital.platform === 'string' ? digital.platform : null,
      techDetected: Array.isArray(digital.techDetected)
        ? digital.techDetected.filter((s): s is string => typeof s === 'string')
        : [],
      mobileExperience: typeof digital.mobileExperience === 'string' ? digital.mobileExperience : null,
    },
    fitAssessment: {
      fitScore: Math.min(5, Math.max(1, Number(fitAssessment.fitScore) || 3)),
      fitRationale: typeof fitAssessment.fitRationale === 'string' ? fitAssessment.fitRationale : null,
      primaryNeeds: Array.isArray(fitAssessment.primaryNeeds)
        ? fitAssessment.primaryNeeds.filter((s): s is string => typeof s === 'string')
        : [],
      opportunities: Array.isArray(fitAssessment.opportunities)
        ? fitAssessment.opportunities.filter((s): s is string => typeof s === 'string')
        : [],
    },
    outreachAngles: Array.isArray(p.outreachAngles)
      ? (p.outreachAngles as Array<Record<string, unknown>>)
          .filter(a => typeof a.hook === 'string' && typeof a.message === 'string')
          .map(a => ({ hook: a.hook as string, message: a.message as string }))
      : [],
  }
}

// Build complete intelligence object from raw data + AI output
export function buildCompanyIntelligence(
  company: { id: string; name: string; domain?: string },
  rawData: { website: WebsiteData | null; companiesHouse: CompaniesHouseData | null },
  aiOutput: AIOutput
): CompanyIntelligence {
  const ch = rawData.companiesHouse?.company
  const officers = rawData.companiesHouse?.officers || []

  return {
    generatedAt: new Date().toISOString(),
    confidence: calculateConfidence(rawData),
    sources: buildSourcesList(rawData),

    overview: aiOutput.overview,

    firmographics: {
      legalName: ch?.company_name || null,
      companyNumber: ch?.company_number || null,
      status: ch?.company_status || null,
      incorporatedDate: ch?.date_of_creation || null,
      companyType: ch?.type || null,
      sicCodes: ch?.sic_codes || [],
      sector: aiOutput.sector,
      registeredAddress: formatAddress(ch?.registered_office_address),
      website: company.domain || null,
    },

    size: {
      turnoverBand: null,
      employeeEstimate: null,
      accountsCategory: ch?.accounts?.accounting_reference_date ? 'Filed' : null,
      latestFilingDate: ch?.last_accounts?.made_up_to || null,
      growthSignals: aiOutput.growthSignals,
    },

    directors: officers
      .filter(o => o.officer_role === 'director' && !o.resigned_on)
      .map(o => ({
        name: o.name,
        role: o.officer_role,
        appointedDate: o.appointed_on || null,
      })),

    digital: {
      ...aiOutput.digital,
    },

    fitAssessment: aiOutput.fitAssessment,
    outreachAngles: aiOutput.outreachAngles,
  }
}

function calculateConfidence(rawData: {
  website: WebsiteData | null
  companiesHouse: CompaniesHouseData | null
}): 'low' | 'medium' | 'medium-high' | 'high' {
  let score = 0
  if (rawData.website) score += 2
  if (rawData.companiesHouse?.company) score += 2

  if (score >= 4) return 'high'
  if (score >= 3) return 'medium-high'
  if (score >= 2) return 'medium'
  return 'low'
}

function buildSourcesList(rawData: {
  website: WebsiteData | null
  companiesHouse: CompaniesHouseData | null
}): DataSource[] {
  return [
    {
      name: 'website',
      status: rawData.website ? 'success' : 'failed',
      fetchedAt: rawData.website?.fetchedAt || null,
      reference: rawData.website?.url || null,
    },
    {
      name: 'companiesHouse',
      status: rawData.companiesHouse?.company ? 'success' : 'failed',
      fetchedAt: rawData.companiesHouse?.fetchedAt || null,
      reference: rawData.companiesHouse?.company?.company_number || null,
    },
  ]
}

function formatAddress(address?: {
  address_line_1?: string
  address_line_2?: string
  locality?: string
  postal_code?: string
  country?: string
}): string | null {
  if (!address) return null

  return [address.address_line_1, address.address_line_2, address.locality, address.postal_code, address.country]
    .filter(Boolean)
    .join(', ')
}

// Build partial intelligence when AI is not available
export function buildPartialIntelligence(
  company: { id: string; name: string; domain?: string },
  rawData: { website: WebsiteData | null; companiesHouse: CompaniesHouseData | null }
): CompanyIntelligence {
  const ch = rawData.companiesHouse?.company
  const officers = rawData.companiesHouse?.officers || []

  return {
    generatedAt: new Date().toISOString(),
    confidence: calculateConfidence(rawData),
    sources: buildSourcesList(rawData),

    overview: 'AI summary not available. Configure an AI provider in Settings to enable.',

    firmographics: {
      legalName: ch?.company_name || null,
      companyNumber: ch?.company_number || null,
      status: ch?.company_status || null,
      incorporatedDate: ch?.date_of_creation || null,
      companyType: ch?.type || null,
      sicCodes: ch?.sic_codes || [],
      sector: null,
      registeredAddress: formatAddress(ch?.registered_office_address),
      website: company.domain || null,
    },

    size: {
      turnoverBand: null,
      employeeEstimate: null,
      accountsCategory: ch?.accounts?.accounting_reference_date ? 'Filed' : null,
      latestFilingDate: ch?.last_accounts?.made_up_to || null,
      growthSignals: [],
    },

    directors: officers
      .filter(o => o.officer_role === 'director' && !o.resigned_on)
      .map(o => ({
        name: o.name,
        role: o.officer_role,
        appointedDate: o.appointed_on || null,
      })),

    digital: {
      platform: null,
      techDetected: [],
      mobileExperience: null,
    },

    fitAssessment: {
      fitScore: 3,
      fitRationale: null,
      primaryNeeds: [],
      opportunities: [],
    },

    outreachAngles: [],
  }
}

// Export raw data structure for storage
export function buildRawData(
  website: WebsiteData | null,
  companiesHouse: CompaniesHouseData | null
): ResearchRawData {
  return {
    fetchedAt: new Date().toISOString(),
    website,
    companiesHouse,
  }
}
